import { useEffect, useMemo, useRef, useState } from 'react'

import { getProductById, Brand } from '@fe-sdk/cbx-products'
import type {
  Asset,
  ProductDataVariant
} from '../../../../services/product/product-data'
import {
  getProductImageCache,
  setProductImageCache,
  ProductImageMap
} from '../../../../services/auto-delivery/product-image-cache'
import { getAssetImageUrl } from '../../../../utils/asset-url'
import { getPreviewHeaders } from '../../../../utils/api-headers'
import type { CartProductEntry } from '../../common/cart-management/cartConfig'
import type { CartLocalStorageJSON } from '../../common/cart-management/types'
import type { GroupedItem } from '../models'
import {
  AUTODELIVERY_ORDER_TYPEID,
  AUTODELIVERY_RECCURENT_TYPEID
} from '../../../../utils/constants'

interface UseAutoDeliveryProductImagesParams {
  isAutoDeliveryEnabled: boolean
  data: { items: GroupedItem[] } | undefined
  cartLocalStorageJSON: CartLocalStorageJSON | null | undefined
  config: {
    market: string
    channel: string
    cbxUrl: string
    xChannel: string
    locale: string
  }
}

/**
 * Resolves the primary image URL from a product's assets array.
 * Prefers assets classified as "Primary", falls back to any Image asset.
 */
export const resolveImageUrl = (assets?: Asset[]): string | undefined => {
  if (!assets || assets.length === 0) {
    return undefined
  }

  const primaryAsset = assets.find(
    (a) =>
      a.assetType === 'Image' &&
      (a as Asset & { assetClassification?: string }).assetClassification ===
        'Primary'
  )
  const fallbackAsset = assets.find((a) => a.assetType === 'Image')
  const asset = primaryAsset ?? fallbackAsset

  return getAssetImageUrl(asset)
}

/**
 * Manages product image caching for the AutoDelivery sticky bar.
 *
 * - Populates session storage from current PLP product data on mount
 * - Detects cart items missing from cache and fetches them individually
 *   via getProductById (temporary solution until a dedicated bulk API exists)
 * - Returns a flat map of globalArticleCode → imageUrl for O(1) lookups
 */
export const useAutoDeliveryProductImages = (
  params: UseAutoDeliveryProductImagesParams
): ProductImageMap => {
  const { isAutoDeliveryEnabled, data, cartLocalStorageJSON, config } = params

  const [imageCache, setImageCache] =
    useState<ProductImageMap>(getProductImageCache)
  const fetchInProgress = useRef(false)

  const variantByCode = useMemo(() => {
    const map = new Map<string, ProductDataVariant>()
    data?.items?.forEach((item: GroupedItem) => {
      item?.variants?.forEach((variant: ProductDataVariant) => {
        if (variant?.globalArticleCode) {
          map.set(variant.globalArticleCode, variant)
        }
      })
    })
    return map
  }, [data?.items])

  const cartEntries = useMemo(() => {
    const orderTypeId = cartLocalStorageJSON?.cart?.orderType?.typeId
    const isADCart =
      orderTypeId === AUTODELIVERY_ORDER_TYPEID ||
      orderTypeId === AUTODELIVERY_RECCURENT_TYPEID
    if (!isAutoDeliveryEnabled || !isADCart) {
      return []
    }
    return cartLocalStorageJSON?.cart?.entries ?? []
  }, [
    isAutoDeliveryEnabled,
    cartLocalStorageJSON?.cart?.orderType?.typeId,
    cartLocalStorageJSON?.cart?.entries
  ])

  // Populate cache from current PLP's variant data
  useEffect(() => {
    if (!isAutoDeliveryEnabled || variantByCode.size === 0) {
      return
    }

    const currentCache = getProductImageCache()
    const newEntries: ProductImageMap = {}
    let hasNew = false

    variantByCode.forEach((variant, code) => {
      if (currentCache[code]) {
        return
      }
      const url = getAssetImageUrl(variant?.assets?.[0])
      if (url) {
        newEntries[code] = url
        hasNew = true
      }
    })

    if (hasNew) {
      setImageCache((prev) => {
        const merged = { ...prev, ...newEntries }
        setProductImageCache(newEntries)
        return merged
      })
    }
  }, [isAutoDeliveryEnabled, variantByCode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch missing images individually via getProductById
  useEffect(() => {
    if (!isAutoDeliveryEnabled || cartEntries.length === 0) {
      return
    }

    // Wait for PLP product data to load before determining what's missing.
    // prevents redundant fetches for products visible on the page
    if (data === undefined) {
      return
    }

    const currentCache = getProductImageCache()
    const missingCodes = cartEntries
      .map((entry: CartProductEntry) => entry?.product?.code)
      .filter((code): code is string => typeof code === 'string')
      .filter((code) => !currentCache[code] && !variantByCode.has(code))

    if (missingCodes.length === 0 || fetchInProgress.current) {
      return
    }

    const fetchMissingImages = async () => {
      fetchInProgress.current = true
      try {
        const customHeaders = getPreviewHeaders()
        const newEntries: ProductImageMap = {}
        let hasNew = false

        const fetchPromises = missingCodes.map(async (code) => {
          try {
            const response = await getProductById({
              productId: code,
              baseURL: config?.cbxUrl ?? '',
              brand: 'IQOS' as Brand,
              channel:
                config?.channel?.toLowerCase() === 'web'
                  ? 'web'
                  : (config?.channel ?? ''),
              xChannel: config?.xChannel || 'WEBSITE',
              countryCode: config?.market?.toUpperCase() ?? '',
              market: config?.market ?? '',
              locale: config?.locale,
              includeVariants: false,
              includeRelationships: false,
              includeOffline: false,
              includeCrossLinked: false,
              ...(customHeaders ? { customHeaders } : {})
            })

            if (!response || 'error' in response || !response.data) {
              return
            }

            const productData =
              'data' in response.data ? response.data.data : response.data
            const assets = (productData as { assets?: Asset[] })?.assets
            const url = resolveImageUrl(assets)

            if (url) {
              newEntries[code] = url
              hasNew = true
            }
          } catch {
            // Silently skip individual product fetch failures
          }
        })

        await Promise.all(fetchPromises)

        if (hasNew) {
          setImageCache((prev) => {
            const merged = { ...prev, ...newEntries }
            setProductImageCache(newEntries)
            return merged
          })
        }
      } finally {
        fetchInProgress.current = false
      }
    }

    fetchMissingImages()
  }, [isAutoDeliveryEnabled, cartEntries, variantByCode, config]) // eslint-disable-line react-hooks/exhaustive-deps

  return imageCache
}
